import type { Vector3Json } from 'contracts/basic';
import { Vector3, type Group } from 'three';

import {
  type CompositeProgressController,
  type AnyProgressSignal,
  AbortError,
} from '@frontend/controllers';
import {
  WaitUntilSetController,
  microWait,
  addEventListenerAndCollectRemovers,
  runRemoveEventListeners,
} from '@frontend/utils';

import { PART_TYPES } from '../constants';
import type { PartJson, OptimalBoundingBoxJson } from '../contracts/part';
import type { TessJson, STLTessJson } from '../contracts/tess';
import {
  createAdditionalFeedback,
  disposeAdditionalFeedback,
} from '../renderer/creation-utils';
import type { FeatureOrFeedBackItem } from '../renderer/creation-utils';
import type {
  AsyncLoadable,
  SyncLoadable,
} from '../renderer/loadingPriorityQueue';
import type { Renderer } from '../renderer/renderer';
import { viewerDataService } from '../viewerDataService';
import { OptimalBoundingBoxGeometry } from './OptimalBoundingBoxGeometry';
import { dxfService } from './dxf/service';
import type { PMIData } from './pmi/PMIData';
import { STEPGeometry } from './step/STEPGeometry';
import type { MetaDataType } from './step/STEPGeometry';
import { STLGeometry } from './stl/STLGeometry';

type GeometryFromPartType<T extends PART_TYPES> = T extends PART_TYPES.STL
  ? STLGeometry
  : T extends PART_TYPES.STEP
  ? STEPGeometry
  : T extends PART_TYPES.DXF
  ? STEPGeometry
  : never;

export type PartGeometryToGet = {
  partFileUuid: string;
  authenticatedUrl: string;
  partName: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyPart = Part<any>;

export class Part<T extends PART_TYPES> implements AsyncLoadable, SyncLoadable {
  public static readonly suffix = '-part';
  public readonly id?: string;
  public readonly name?: string;
  public readonly optimalBoundingBoxGeometry: OptimalBoundingBoxGeometry | null;
  public flatPartFileUuid?: string; //Not readonly so viewerDataService can update if needed
  public readonly derivedFromFileUuid?: string;
  public readonly isDerivedFromInterrogation?: boolean;
  public additionalFeedback: Record<string, Group> = {}; //populated by this.createAdditionalFeedback
  public pmi?: PMIData;
  private boxFound = false;
  private authenticatedUrl?: string;
  private _type?: T;
  private _geometry?: GeometryFromPartType<T>;
  private resumeController?: WaitUntilSetController<void>;
  private loaded?: Promise<number>; //presence indicates this.isLoading. Resolves when loaded
  private _progress?: CompositeProgressController;
  private _boxMin: Vector3 = new Vector3(Infinity, Infinity, Infinity); //reset by this.setBoxBounds
  private _boxMax: Vector3 = new Vector3(-Infinity, -Infinity, -Infinity); //reset by this.setBoxBounds
  private _featuresOrFeedbackItems?: Iterable<FeatureOrFeedBackItem>;
  private disposeFns: (() => void)[] = [];
  private isDisposed?: boolean;

  constructor(
    data: PartJson<T>,
    private renderer: Renderer,
    parentProgress?: CompositeProgressController,
    public signal?: AbortSignal,
    existingGeometry: GeometryFromPartType<T> | null = null,
    existingGeometryType: PART_TYPES = PART_TYPES.STEP,
  ) {
    this.id = data.partFileUuid || data.uuid; //pcad provides uuid
    this.name = data.name;
    const optimalBoundingBoxData: OptimalBoundingBoxJson | undefined =
      data.optimalBoundingBoxData || data.optimalBoundingBox;
    this.optimalBoundingBoxGeometry = optimalBoundingBoxData
      ? OptimalBoundingBoxGeometry.fromRawData(optimalBoundingBoxData)
      : null;

    //With MBOM work, we stopped providing flatPartUuid in json. Now, new with PP-10014
    //flatFileId is provided.
    this.flatPartFileUuid = data.flatPartUuid || data.flatFileId;
    //The part for the flatFileId points to has isDerivedFromInterrogation=true
    this.isDerivedFromInterrogation = data.isDerivedFromInterrogation;

    if (data.derivedFromFileId) {
      //Note: the part with derivedFromFileUuid, will have its `flatPartFileUuid` set in viewerDataService
      //because it likely is in indexedDB cache and `flatPartFileUuid` did not exist until this part
      //was created.
      this.derivedFromFileUuid = data.derivedFromFileId;
    }

    //Side note: this._progress is pushed here to parentProgress, but it is also pushed to each of its body's progress too.
    //Because hydrating a body requires hydrating the part.
    this._progress = parentProgress?.pushCompositeProgress({
      id: viewerDataService.getPartLoadingId(this.id as string),
      description: `Loading Part ${this.name || this.id}`,
    });
    if (existingGeometry) {
      this._type = (
        existingGeometry instanceof STLGeometry
          ? PART_TYPES.STL
          : existingGeometry instanceof STEPGeometry
          ? existingGeometryType
          : existingGeometryType
      ) as T;
      this._geometry = existingGeometry;
      const geometrySignal = existingGeometry.progress;
      if (geometrySignal && !existingGeometry.isLoaded) {
        this._progress?.push({
          signal: geometrySignal,
        });
      }
      this._progress?.close();
    } else {
      //Note: usually data.tess is not defined when part data came from backend
      //* Instead it will provide authenticatedUrl. But before trying to fetch from authenticatedUrl, fromCloneable() will be used
      if (data.tess) {
        //Then the geometry can be created now (still have to hydrate with load() or loadAsync() later)
        const tess: TessJson | STLTessJson =
          typeof data.tess === 'string'
            ? dxfService.importDxfContent(this.id as string, data.tess)
            : data.tess;
        this.createUnhydratedGeometry(tess);
      } else {
        //otherwise cache url until we are ready to get tess data
        this.authenticatedUrl = data.authenticatedUrl;
      }
    }
    if (signal) {
      addEventListenerAndCollectRemovers(
        this.disposeFns,
        signal,
        'abort',
        () => {
          this.dispose();
        },
      );
    }
  }

  public dispose() {
    if (this.isDisposed) return;
    this.isDisposed = true;
    runRemoveEventListeners(this.disposeFns);
    this.abortLoad();
    disposeAdditionalFeedback(this.additionalFeedback);
    if (this._featuresOrFeedbackItems) {
      delete this._featuresOrFeedbackItems;
    }
    if (this.pmi) {
      this.pmi.dispose();
    }
    if (this._geometry) {
      this._geometry.dispose();
    }
    if (this.optimalBoundingBoxGeometry) {
      this.optimalBoundingBoxGeometry.dispose();
    }
  }

  //hasGeometry means has tesselated data
  public get hasGeometry(): boolean {
    return !!(this._geometry || this.authenticatedUrl);
  }

  public get partDataToLoad(): PartGeometryToGet | undefined {
    if (this.authenticatedUrl && this.id) {
      return {
        partFileUuid: this.id,
        partName: this.name ?? 'part',
        authenticatedUrl: this.authenticatedUrl,
      };
    }
  }

  public get geometry(): GeometryFromPartType<T> {
    if (!this._geometry) {
      throw new Error(
        this.authenticatedUrl
          ? 'Part geometry has not been fetched'
          : 'Part has no geometry',
      );
    }
    return this._geometry;
  }

  public get type(): T {
    if (!this._type) {
      throw new Error(
        this.authenticatedUrl
          ? 'Part geometry has not been fetched'
          : 'Part has no geometry',
      );
    }
    return this._type;
  }

  public get hasType(): boolean {
    return this._type !== undefined;
  }

  public get isLoaded(): boolean {
    return (this.hasGeometry && this._geometry?.isLoaded) || false;
  }

  public get isLoading(): boolean {
    return !!this.loaded || this._geometry?.isLoading || false;
  }

  public get progress(): AnyProgressSignal | undefined {
    return this._progress?.signal;
  }

  public pause() {
    if (!this.isLoaded && !this.signal?.aborted) {
      if (!this.resumeController) {
        this.resumeController = new WaitUntilSetController(this.signal);
      }
      if (this._geometry) {
        this._geometry.pause();
      }
    }
  }

  public abortLoad() {
    if (this.loaded) {
      this.pause(); //force pause so we can reject
      this.loaded.catch(() => Promise.resolve()); //the promise's rejection should be handled at least once
      delete this.loaded;
    }
    if (this.resumeController) {
      this.resumeController.setReject();
      delete this.resumeController;
    }
    if (this._geometry) {
      this._geometry.abortLoad();
    }
    if (this._progress) {
      this._progress.close();
      delete this._progress;
    }
  }

  public get isPaused(): boolean {
    return !!this.resumeController || this._geometry?.isPaused || false;
  }

  public resume() {
    if (!this.signal?.aborted) {
      if (this.resumeController) {
        this.resumeController.set();
        delete this.resumeController;
      }
      if (this._geometry) {
        this._geometry.resume();
      }
    }
  }

  public load(): void {
    if (!this._geometry) {
      throw new Error(
        'Cannot load geometry synchronously that has not been fetched',
      );
    } else if (this.isLoading) {
      throw new Error(
        'Cannot load part synchronously that already started async loading',
      );
    }
    this._geometry.load();
    if (this._progress) {
      this._progress.close(); //should already be closed (but just in case)
      delete this._progress;
    }
    //shouldn't be any pending geometry or fetch, but remove just in case
    viewerDataService.removePendingGeometry(this.id as string);
    viewerDataService.removePendingFetch(this.id as string);
    this.resume(); //cleanup resumeController if it exists
  }

  public loadAsync(lastPause: number = performance.now()): Promise<number> {
    if (!this.hasGeometry || !this.id) {
      return Promise.resolve(lastPause);
    }
    if (this._geometry) {
      return this._geometry.loadAsync(lastPause);
    }
    if (!this.loaded) {
      this.loaded = (async () => {
        try {
          //Try loading cloneable tesselated data from IndexedDB cache
          const cloneableTessData = await viewerDataService.getGeometry(
            this.id as string,
            this.name || 'part',
            this._progress,
          );
          if (this.resumeController) {
            await this.resumeController.waitUntilSet;
            //eslint-disable-next-line no-param-reassign
            lastPause = performance.now();
          }
          const now = performance.now();
          if (now - lastPause > 50) {
            await microWait();
            //eslint-disable-next-line no-param-reassign
            lastPause = now;
          }
          if (this.signal?.aborted) {
            throw new AbortError(
              `Loading part ${this.id} aborted because an abort signal was received.`,
            );
          }
          if (cloneableTessData) {
            if (cloneableTessData.type === PART_TYPES.STL) {
              this._geometry = STLGeometry.fromCloneable(
                cloneableTessData,
              ) as GeometryFromPartType<T>;
              this._type = PART_TYPES.STL as T;
            } else if (cloneableTessData.type === PART_TYPES.STEP) {
              this._geometry = STEPGeometry.fromCloneable(
                cloneableTessData,
              ) as GeometryFromPartType<T>;
              this._type = PART_TYPES.STEP as T;
            }
          }
          //Failed to load tessellated data from IndexedDB cache, so try fetching from authenticatedUrl
          if (!this._geometry && this.authenticatedUrl) {
            const name = this.name || 'part';
            //TODO: For dxf, need to fetch the DXF
            const tess = await viewerDataService.fetchTesslatedData(
              this.id as string,
              this.authenticatedUrl as string,
              name,
              this._progress,
              this.signal,
            )[0];
            if (this.resumeController) {
              await this.resumeController.waitUntilSet;
              //eslint-disable-next-line no-param-reassign
              lastPause = performance.now();
            }
            if (this.signal?.aborted) {
              throw new AbortError(
                `Loading part ${this.id} aborted because an abort signal was received.`,
              );
            }
            if (tess) {
              const geometry = this.createUnhydratedGeometry(tess);
              if (geometry) {
                this._progress?.close();
                //eslint-disable-next-line no-param-reassign
                lastPause = await geometry.loadAsync(lastPause);
                //cache geometry for future use (defer caching and don't wait)
                setTimeout(async () => {
                  await viewerDataService.cacheTesslatedData(
                    this.id as string,
                    geometry.toCloneable(),
                    name,
                  );
                  viewerDataService.removePendingFetch(this.id as string);
                }, 0);
              }
            }
          }
        } finally {
          delete this.authenticatedUrl; //finished trying to get geometry from IndexedDB or fetching
          delete this.loaded;
          if (this._progress) {
            this._progress.close(); //should already be closed (but just in case)
            delete this._progress;
          }
          if (
            this._featuresOrFeedbackItems &&
            (!this.signal || !this.signal.aborted)
          ) {
            this.createAdditionalFeedback(this._featuresOrFeedbackItems);
            delete this._featuresOrFeedbackItems;
          }
          viewerDataService.removePendingGeometry(this.id as string);
          viewerDataService.removePendingFetch(this.id as string);
          this.resume(); //cleanup resumeController if it exists
        }
        return lastPause;
      })();
    } else if (this.isPaused) {
      this.resume();
    }
    return this.loaded;
  }

  public createAdditionalFeedback(
    featuresOrFeedbackItems: Iterable<FeatureOrFeedBackItem>,
  ): boolean {
    if (this.isLoaded) {
      return createAdditionalFeedback(
        this,
        featuresOrFeedbackItems,
        this.renderer,
      );
    } else {
      if (featuresOrFeedbackItems) {
        this._featuresOrFeedbackItems = featuresOrFeedbackItems; //cache so featuresOrFeedbackItems can be added when this part is loaded
      }
    }
    return false;
  }

  public setBoxBounds(): void {
    if (this.boxFound) return;
    if (this.type === PART_TYPES.STL) {
      const geometry = this._geometry as STLGeometry;
      const boundingBox = geometry.boundingBox;
      if (boundingBox) {
        this._boxMin = boundingBox.min;
        this._boxMax = boundingBox.max;
      }
    } else {
      const geometry = this._geometry as STEPGeometry;
      for (const {
        boundBox: { min, max },
      } of geometry.faceMetaData) {
        this.replaceVector(this._boxMin, min, true);
        this.replaceVector(this._boxMax, max, false);
      }
      if (!geometry.faceMetaData.length) {
        for (const {
          boundBox: { min, max },
        } of geometry.edgeMetaData) {
          this.replaceVector(this._boxMin, min, true);
          this.replaceVector(this._boxMax, max, false);
        }
      }
    }
    this.boxFound = true;
  }

  public get boxMin(): Vector3 {
    return this._boxMin;
  }

  public get boxMax(): Vector3 {
    return this._boxMax;
  }

  public getEntityMeta(index: number, type: MetaDataType) {
    const geometry = this.geometry;
    if (geometry instanceof STEPGeometry) {
      return geometry.getEntityMeta(index, type);
    }
  }

  private createUnhydratedGeometry(
    tess: TessJson | STLTessJson,
  ): GeometryFromPartType<T> | undefined {
    this._type = tess.Type as T;

    const id = `${this.id}-partGeometry`;

    const [geometry, geometryProgress] =
      this._type === PART_TYPES.STEP || this._type === PART_TYPES.DXF
        ? STEPGeometry.fromJSON(
            tess as TessJson,
            id,
            this.name || 'part',
            this.signal,
          )
        : STLGeometry.fromJSON(
            tess as STLTessJson,
            id,
            this.name || 'part',
            this.signal,
          );

    this._progress?.push({ signal: geometryProgress });
    this._progress?.close();
    this._geometry = geometry as GeometryFromPartType<T>;
    return this._geometry;
  }

  private replaceVector(
    host: Vector3,
    [x, y, z]: Vector3Json,
    lessThan = true,
  ) {
    if (lessThan) {
      if (x < host.x) {
        host.setX(x);
      }
      if (y < host.y) {
        host.setY(y);
      }
      if (z < host.z) {
        host.setZ(z);
      }
    } else {
      if (x > host.x) {
        host.setX(x);
      }
      if (y > host.y) {
        host.setY(y);
      }
      if (z > host.z) {
        host.setZ(z);
      }
    }
  }
}
export default Part;
